<!-- A 端 · 挂供往来预览（§6.1 主从：店×供分页 + 只抽屉明细） -->
<template>
  <ContentWrap>
    <el-alert
      class="mb-12px"
      type="warning"
      :closable="false"
      show-icon
      title="应付供方预估 · 非结算"
      description="按店×供扫应付；点「明细」下钻订单行。汇总应付只计合同结算价快照。请选择完成时间（默认近 30 天）。货款线下结；出账请到「外发结算单」。"
    />

    <el-form :inline="true" :model="queryParams" class="-mb-15px" label-width="80px">
      <el-form-item label="完成时间" required>
        <el-date-picker
          v-model="queryParams.finishTime"
          type="daterange"
          value-format="YYYY-MM-DD"
          start-placeholder="起"
          end-placeholder="止"
          class="!w-280px"
          :clearable="true"
        />
      </el-form-item>
      <el-form-item label="卖货店">
        <el-select
          v-model="queryParams.shopId"
          class="!w-200px"
          clearable
          filterable
          remote
          reserve-keyword
          placeholder="店名/ID 搜索"
          :remote-method="searchShop"
          :loading="shopLoading"
        >
          <el-option
            v-for="s in shopOptions"
            :key="s.id"
            :label="`${s.shopName || s.name || ''} (#${s.id})`"
            :value="s.id"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="供应商">
        <el-select
          v-model="queryParams.supplierId"
          class="!w-200px"
          clearable
          filterable
          remote
          reserve-keyword
          placeholder="供名/ID 搜索"
          :remote-method="searchSupplier"
          :loading="supplierLoading"
        >
          <el-option
            v-for="s in supplierOptions"
            :key="s.id"
            :label="`${s.name || ''} (#${s.id})`"
            :value="s.id"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="订单号">
        <el-input v-model="queryParams.orderNo" class="!w-180px" clearable />
      </el-form-item>
      <el-form-item>
        <el-button type="primary" @click="handleQuery">搜索</el-button>
        <el-button @click="resetQuery">重置</el-button>
      </el-form-item>
    </el-form>

    <el-row :gutter="16" class="mb-16px">
      <el-col :span="6">
        <div class="stat-card">
          <div class="stat-label">可出账行 / 总行</div>
          <div class="stat-value">{{ summary.billableLineCount ?? 0 }} / {{ summary.lineCount ?? 0 }}</div>
        </div>
      </el-col>
      <el-col :span="6">
        <div class="stat-card">
          <div class="stat-label">成交实付合计</div>
          <div class="stat-value">¥{{ fenToYuan(summary.payPriceSum) }}</div>
        </div>
      </el-col>
      <el-col :span="6">
        <div class="stat-card">
          <div class="stat-label">应付供（可出账）</div>
          <div class="stat-value">¥{{ fenToYuan(summary.payableSum) }}</div>
        </div>
      </el-col>
      <el-col :span="6">
        <div class="stat-card">
          <div class="stat-label">毛利差（可出账）</div>
          <div class="stat-value">¥{{ fenToYuan(summary.marginSum) }}</div>
        </div>
      </el-col>
    </el-row>

    <el-divider content-position="left">店 × 供 汇总（共 {{ groupTotal }} 组）</el-divider>
    <el-table v-loading="groupLoading" :data="groups" stripe>
      <el-table-column label="卖货店" min-width="140" show-overflow-tooltip>
        <template #default="{ row }">{{ row.shopName || '#' + row.shopId }}</template>
      </el-table-column>
      <el-table-column label="供应商" min-width="140" show-overflow-tooltip>
        <template #default="{ row }">{{ row.supplierName || '#' + row.supplierId }}</template>
      </el-table-column>
      <el-table-column label="可出账/总行" width="100">
        <template #default="{ row }">{{ row.billableLineCount ?? 0 }} / {{ row.lineCount ?? 0 }}</template>
      </el-table-column>
      <el-table-column label="成交实付" width="110">
        <template #default="{ row }">{{ fenToYuan(row.payPriceSum) }}</template>
      </el-table-column>
      <el-table-column label="应付供(可出账)" width="120">
        <template #default="{ row }">{{ fenToYuan(row.payableSum) }}</template>
      </el-table-column>
      <el-table-column label="毛利差" width="110">
        <template #default="{ row }">{{ fenToYuan(row.marginSum) }}</template>
      </el-table-column>
      <el-table-column label="仅参考行" prop="missingSettlePriceCount" width="90" />
      <el-table-column label="操作" width="90" fixed="right">
        <template #default="{ row }">
          <el-button link type="primary" @click="openDrawer(row)">明细</el-button>
        </template>
      </el-table-column>
    </el-table>
    <Pagination
      v-model:limit="queryParams.groupPageSize"
      v-model:page="queryParams.groupPageNo"
      :total="groupTotal"
      @pagination="loadSummary"
    />

    <el-drawer v-model="drawerVisible" :title="drawerTitle" size="760px" destroy-on-close>
      <div v-if="drawerMeta" class="drawer-meta mb-12px">
        可出账应付 ¥{{ fenToYuan(drawerMeta.payableSum) }} · 行
        {{ drawerMeta.billableLineCount ?? 0 }}/{{ drawerMeta.lineCount ?? 0 }}
      </div>
      <el-table v-loading="drawerLoading" :data="drawerList" stripe size="small">
        <el-table-column label="订单号" prop="orderNo" min-width="150" show-overflow-tooltip />
        <el-table-column label="商品" prop="spuName" min-width="140" show-overflow-tooltip />
        <el-table-column label="数量" prop="count" width="60" />
        <el-table-column label="成交实付" width="90">
          <template #default="{ row }">{{ fenToYuan(row.payPrice) }}</template>
        </el-table-column>
        <el-table-column label="结算单价" width="90">
          <template #default="{ row }">
            {{ row.settleUnitPrice != null ? fenToYuan(row.settleUnitPrice) : '—' }}
          </template>
        </el-table-column>
        <el-table-column label="价来源" prop="settlePriceSourceName" min-width="120" />
        <el-table-column label="可出账" width="70">
          <template #default="{ row }">
            <el-tag :type="row.billable ? 'success' : 'info'" size="small">
              {{ row.billable ? '是' : '否' }}
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column label="应付供" width="90">
          <template #default="{ row }">
            {{ row.payableAmount != null ? fenToYuan(row.payableAmount) : '—' }}
          </template>
        </el-table-column>
        <el-table-column label="毛利差" width="80">
          <template #default="{ row }">
            {{ row.marginAmount != null ? fenToYuan(row.marginAmount) : '—' }}
          </template>
        </el-table-column>
      </el-table>
      <Pagination
        v-model:limit="drawerQuery.pageSize"
        v-model:page="drawerQuery.pageNo"
        :total="drawerTotal"
        @pagination="loadDrawerLines"
      />
    </el-drawer>
  </ContentWrap>
</template>

<script setup lang="ts">
import dayjs from 'dayjs'
import { ElMessage } from 'element-plus'
import { getShopPage } from '@/api/merchant/shop-manager'
import { getSupplierPage } from '@/api/mall/product/supplier'
import {
  getAdminSettlePreviewPage,
  getAdminSettlePreviewSummary
} from '@/api/mall/trade/supplierSettlePreview'

defineOptions({ name: 'TradeSupplierSettlePreview' })

const fenToYuan = (fen?: number | null) => ((fen ?? 0) / 100).toFixed(2)

const defaultFinishRange = (): string[] => [
  dayjs().subtract(29, 'day').format('YYYY-MM-DD'),
  dayjs().format('YYYY-MM-DD')
]

const queryParams = reactive({
  groupPageNo: 1,
  groupPageSize: 20,
  shopId: undefined as number | undefined,
  supplierId: undefined as number | undefined,
  orderNo: undefined as string | undefined,
  finishTime: defaultFinishRange() as string[] | undefined
})

const summary = ref<any>({})
const groups = ref<any[]>([])
const groupTotal = ref(0)
const groupLoading = ref(false)

const shopOptions = ref<any[]>([])
const shopLoading = ref(false)
const supplierOptions = ref<any[]>([])
const supplierLoading = ref(false)

const drawerVisible = ref(false)
const drawerLoading = ref(false)
const drawerList = ref<any[]>([])
const drawerTotal = ref(0)
const drawerTitle = ref('订单行明细')
const drawerMeta = ref<any>(null)
const drawerQuery = reactive({
  pageNo: 1,
  pageSize: 20,
  shopId: undefined as number | undefined,
  supplierId: undefined as number | undefined
})

const finishTimeParam = () => {
  if (!queryParams.finishTime || queryParams.finishTime.length !== 2) {
    return null
  }
  return [
    `${queryParams.finishTime[0]} 00:00:00`,
    `${queryParams.finishTime[1]} 23:59:59`
  ]
}

const ensureFinishTime = () => {
  const ft = finishTimeParam()
  if (!ft) {
    ElMessage.warning('请选择完成时间（默认近 30 天，不支持无时间全量查询）')
    return null
  }
  return ft
}

const summaryParams = () => {
  const finishTime = ensureFinishTime()
  if (!finishTime) return null
  const p: any = {
    groupPageNo: queryParams.groupPageNo,
    groupPageSize: queryParams.groupPageSize,
    orderNo: queryParams.orderNo,
    finishTime
  }
  if (queryParams.shopId) p.shopId = queryParams.shopId
  if (queryParams.supplierId) p.supplierId = queryParams.supplierId
  return p
}

const loadSummary = async () => {
  const p = summaryParams()
  if (!p) {
    groups.value = []
    groupTotal.value = 0
    summary.value = {}
    return
  }
  groupLoading.value = true
  try {
    const data = (await getAdminSettlePreviewSummary(p)) || {}
    summary.value = data
    groups.value = data.groups || []
    groupTotal.value = data.groupTotal ?? (data.groups || []).length
  } finally {
    groupLoading.value = false
  }
}

const handleQuery = () => {
  queryParams.groupPageNo = 1
  loadSummary()
}

const resetQuery = () => {
  queryParams.shopId = undefined
  queryParams.supplierId = undefined
  queryParams.orderNo = undefined
  queryParams.finishTime = defaultFinishRange()
  queryParams.groupPageNo = 1
  handleQuery()
}

const searchShop = async (keyword: string) => {
  shopLoading.value = true
  try {
    const q: any = { pageNo: 1, pageSize: 20 }
    const k = (keyword || '').trim()
    if (k && !/^\d+$/.test(k)) q.shopName = k
    const data = await getShopPage(q)
    let list = data?.list || []
    // 店铺分页无 id 条件：纯数字时补一条可选 ID
    if (k && /^\d+$/.test(k)) {
      const id = Number(k)
      if (!list.some((x: any) => x.id === id)) {
        list = [{ id, shopName: `店铺#${id}` }, ...list]
      }
    }
    shopOptions.value = list
  } catch {
    shopOptions.value = []
  } finally {
    shopLoading.value = false
  }
}

const searchSupplier = async (keyword: string) => {
  supplierLoading.value = true
  try {
    const q: any = { pageNo: 1, pageSize: 20 }
    const k = (keyword || '').trim()
    if (k && !/^\d+$/.test(k)) q.name = k
    const data = await getSupplierPage(q)
    let list = data?.list || []
    if (k && /^\d+$/.test(k)) {
      const id = Number(k)
      if (!list.some((x: any) => x.id === id)) {
        list = [{ id, name: `供应商#${id}` }, ...list]
      }
    }
    supplierOptions.value = list
  } catch {
    supplierOptions.value = []
  } finally {
    supplierLoading.value = false
  }
}

const loadDrawerLines = async () => {
  const finishTime = ensureFinishTime()
  if (!finishTime) return
  drawerLoading.value = true
  try {
    const data = await getAdminSettlePreviewPage({
      pageNo: drawerQuery.pageNo,
      pageSize: drawerQuery.pageSize,
      shopId: drawerQuery.shopId,
      supplierId: drawerQuery.supplierId,
      orderNo: queryParams.orderNo,
      finishTime
    })
    drawerList.value = data?.list || []
    drawerTotal.value = data?.total || 0
  } finally {
    drawerLoading.value = false
  }
}

const openDrawer = async (row: any) => {
  const shopLabel = row.shopName || `#${row.shopId}`
  const supplierLabel = row.supplierName || `#${row.supplierId}`
  drawerTitle.value = `订单行明细 · ${shopLabel} × ${supplierLabel}`
  drawerMeta.value = row
  drawerQuery.pageNo = 1
  drawerQuery.shopId = row.shopId
  drawerQuery.supplierId = row.supplierId
  drawerVisible.value = true
  await loadDrawerLines()
}

onMounted(() => {
  searchShop('')
  searchSupplier('')
  handleQuery()
})
</script>

<style scoped>
.stat-card {
  padding: 12px 16px;
  background: var(--el-fill-color-light);
  border-radius: 6px;
}
.stat-label {
  font-size: 13px;
  color: var(--el-text-color-secondary);
}
.stat-value {
  margin-top: 6px;
  font-size: 20px;
  font-weight: 600;
}
.drawer-meta {
  font-size: 13px;
  color: var(--el-text-color-secondary);
}
</style>
